// PremiseExtractor.ts

import { Address } from "uk-clear-addressing";
import { ExtendedExtractedIngredients } from "./model";

export class PremiseExtractor {
    public static revertPremise(premise: string, address: Partial<Address>): ExtendedExtractedIngredients {
        const extracted: ExtendedExtractedIngredients = {
            thoroughfare: address.thoroughfare || "",
            building_number: "",
            building_name: "",
            sub_building_name: "",
            unit: "", // Added field to capture the unit
            po_box: "",
            floor: "",
            wing: "",
            organisation_name: address.organisation_name || "",
            department_name: address.department_name || "",
        };

        if (PremiseExtractor.hasPOBox(premise)) {
            extracted.po_box = PremiseExtractor.extractPOBox(premise);
            return extracted;
        }

        const tokens = premise.split(/,\s*/).filter((token) => token.trim() !== "");

        let currentIndex = 0;

        // Check for sub-building prefix and extract it along with its number
        if (tokens.length > 0 && PremiseExtractor.hasSubBuildingPrefix(tokens[currentIndex])) {
            extracted.sub_building_name = tokens[currentIndex];
            extracted.unit = tokens[currentIndex]; // Assigning sub-building name to the unit field
            currentIndex++;
        }

        // Check for floor information
        if (tokens.length > currentIndex && PremiseExtractor.isFloorInformation(tokens[currentIndex])) {
            extracted.floor = tokens[currentIndex];
            currentIndex++;
        }

        // Check for wing information
        if (tokens.length > currentIndex && PremiseExtractor.isWingInformation(tokens[currentIndex])) {
            extracted.wing = tokens[currentIndex];
            currentIndex++;
        }

        // Check for building number
        if (tokens.length > currentIndex && PremiseExtractor.isBuildingNumber(tokens[currentIndex])) {
            extracted.building_number = tokens[currentIndex];
            currentIndex++;
        }

        // Combine remaining tokens as building name
        extracted.building_name = tokens.slice(currentIndex).join(" ").trim();

        return extracted;
    }

    private static hasPOBox(premise: string): boolean {
        return /PO Box \d+/i.test(premise);
    }

    private static extractPOBox(premise: string): string {
        const match = premise.match(/PO Box (\d+)/i);
        return match ? match[1] : "";
    }

    private static hasSubBuildingPrefix(token: string): boolean {
        const subBuildingPattern = /^(Flat|Apartment|Room|Unit|Block|Suite|Rear of|Shop|Stall|Back of|Building|Maisonette|Maisonettes|Units|Stalls|Shops|Blocks|Suites)\s*\d*/i;
        return subBuildingPattern.test(token);
    }

    private static isFloorInformation(token: string): boolean {
        const floorPattern = /(\d+(st|nd|rd|th)\s+Floor|Ground Floor)/i;
        return floorPattern.test(token);
    }

    private static isWingInformation(token: string): boolean {
        const wingPattern = /(West|East|North|South|Left|Right) Wing/i;
        return wingPattern.test(token);
    }

    private static isBuildingNumber(token: string): boolean {
        const numberPattern = /^\d+[A-Z]?$|^\d+-\d+[A-Z]?$/i;
        return numberPattern.test(token);
    }
}
